import React from 'react';
import PropTypes from 'prop-types';
import Text from '../RTE/Text';
import ErrorComponent from '@components/Error';
import Cta from '@components/CTA';
import Image from '@components/Image';
import useMobileDetection from "@klickinc/kfs-components-use-mobile-detection";
import { HeroBanner } from './Banner.css';

/**
 * Banner Component - Displays promotional content in various layouts
 * @param {Object} props - Component props
 * @param {Object} props.content - Banner content object containing all banner data
 * @returns {React.ReactElement} Rendered banner component
 */
const Banner = ({ content }) => {
	const isMobile = useMobileDetection();

  if (!content) {
    return <ErrorComponent message="Banner content is required" />;
  }

  const {
    items,
	  image,
	  imageMobile,
	  backgroundImage,
	  backgroundColor,
	  type,
	  contentfulid
  } = content;

	const imageUrl = image?.file?.url;
	const imageAlt = image?.file?.fileName || 'Banner image';
	const imageMobileUrl = imageMobile?.file?.url;
	const backgroundImageUrl = backgroundImage?.file?.url;
	const backgroundImageAlt = backgroundImage?.file?.fileName || 'Banner background image';
	const bgColor = backgroundColor || 'transparent';
	const bannerId = contentfulid || '';

  // Base container classes
	//   const containerClasses = 'relative flex items-center banner-container';

  const getCta = (item) => {
    return (
      <Cta
        key={item?.id}
			label={item?.label}
			href={item?.href || '#'}
			target={item?.target}
			displayType={item?.displayType}
			style={item?.style}
			theme={item?.theme}
      />
    );
  };

  const getText = (item) => {
    return (
		<Text
			key={item?.id}
        content={item?.text?.raw}
        classOverlay={item?.classOverlay?.classOverlay}
        marginTopDesktop={item?.marginTopDesktop}
        marginBottomDesktop={item?.marginBottomDesktop}
        marginTopMobile={item?.marginTopMobile}
        marginBottomMobile={item?.marginBottomMobile}
        paddingTopDesktop={item?.paddingTopDesktop}
        paddingBottomDesktop={item?.paddingBottomDesktop}
        paddingTopMobile={item?.paddingTopMobile}
        paddingBottomMobile={item?.paddingBottomMobile}
        paddingLeftDesktop={item?.paddingLeftDesktop}
        paddingRightDesktop={item?.paddingRightDesktop}
        paddingLeftMobile={item?.paddingLeftMobile}
        paddingRightMobile={item?.paddingRightMobile}
        maxWidthDesktop={item?.maxWidthDesktop}
        maxWidthMobile={item?.maxWidthMobile}
        textAlign={item?.textAlign}
        textColor={item?.textColor?.textColor}
        references={item?.references}
        style={item?.style}
        nodeStyles={item?.nodeStyles}
        customRenderers={item?.customRenderers}
        customMarks={item?.customMarks}
      />
    );
  };


  // Full Background Variant (colored background with centered content)
	if (type === 'Home') {
    return (
		<div className='relative lg:flex lg:items-center lg:justify-center'>
			{imageUrl && (
				<Image
					src={imageUrl}
					alt={imageAlt}
					srcMobile={imageMobileUrl}
					pictureClassName="w-full"
					className="w-full rounded-bottom_48"
				/>
			)}

			<div className='px-[30px] py-[30px] lg:max-w-default lg:w-full lg:absolute lg:pl-[40px] lg:ml-[-60px] '>
				<div className="w-full mobileTabletMax:[&>*]:!text-center mobileTabletMax:flex mobileTabletMax:flex-col mobileTabletMax:justify-center lg:w-[475px] lg:mt-[155px]">
					{
						items?.map((item) => {
							if (item.__typename === 'ContentfulRichText') {
								if (item?.contentfulid?.trim() === 'hero-heading-1') {
									return getText({
										...item,
										style: "text-left hidden xl:block",
										nodeStyles: { h1_xl: "text-h1_xl_mobile xl:text-h1_xl font-asap_condensed uppercase text-shadow-xl" }
									});
								} else if (item?.contentfulid?.trim() === 'hero-heading-2') {
									return getText({
										...item,
										style: "text-left hidden xl:block ",
										nodeStyles: { paragraph: " !text-h1_2xl font-asap_condensed uppercase text-shadow-xl" }
									});
								} else if (item?.contentfulid.trim() === 'hero-heading') {
									return getText({
										...item,
										style: "block xl:hidden",
										nodeStyles: { heading2: "text-dark_green !text-h1_lg font-asap_condensed uppercase lg:text-white" }
									});
								} else {
									return getText({
										...item,
										style: "text-left md:my-2 xl:my-5 block",
										nodeStyles: { paragraph: "text-dark_green lg:text-white mt-2 !text-h5 font-bold md:text-h4 font-fields text-center md:pr-8 md:pl-4 lg:text-shadow-xl xl:!text-h1_lg" }
									});
								}
							} else if (item.__typename === 'ContentfulLink') {
								return <div className="flex justify-center mt-5 lg:mt-10">{getCta({ ...item, style: "lg:!text-white !text-dark_green" })}</div>;
							}
							return null;
						})
					}
				</div>
			</div>
      </div>
    );
	}

	if (type === 'Full Background') {
		return (
			<div className="relative">
				{imageUrl && (
					<Image
						src={imageUrl}
						alt={imageAlt}
						srcMobile={imageMobileUrl}
						className="w-full"
					/>
				)}

				<div className="relative lg:absolute h-full lg:pl-[30px] xl:pl-[0] lg:left-[50%] lg:-translate-x-1/2 lg:top-0 lg:flex lg:items-center lg:justify-start w-full lg:max-w-default content">
					<div className="w-full px-[20px] py-[50px] pb-[70px] lg:px-0 lg:max-w-[440px]">
						{
							items.map((item) => {
								if (item.__typename === 'ContentfulRichText') {
									return getText({ ...item, style: "!text-center lg:!text-left" });
								}
								return null;
							})
						}
					</div>
				</div>
			</div>
		);
	}

	if (type === 'Get Access Details') {
		return (
			<div className="relative flex mx-auto flex-col lg:flex-row items-center justify-center lg:max-w-lg xl:max-w-default ">
				<div className="lg:absolute lg:left-[50px] xl:left-[100px] z-[1] flex items-center justify-center ">
					<div className="max-w-[440px] px-[20px] lg:px-0 lg:max-w-[440px]">
						{
							items.map((item) => {
							if (item.__typename === 'ContentfulRichText') {
								return getText(item);
							} else if (item.__typename === 'ContentfulLink') {
								return <div className="flex justify-center mt-10">{getCta(item)}</div>;
							}
							return null;
							})
						}
					</div>
				</div>
				<div className="w-full justify-center relative flex  lg:justify-end">

					{imageUrl &&
						<Image
							src={imageUrl}
						alt={imageAlt}
						srcMobile={imageMobileUrl}
						className=" relative mobileTabletMax:z-[-1] mt-[-80px] lg:mt-0 w-full lg:w-max-content"
						/>
					}
				</div>
			</div>
		);
	}

	if (type === 'Why Exparel Postural Pain') {
		return (
			<div className="relative flex mx-auto flex-col lg:flex-row items-center justify-center lg:max-w-lg xl:max-w-default ">
				<div className="lg:absolute lg:left-0 z-[1] flex items-center justify-center ">
					<div className="mobileTabletMax:[&>*]:!text-center mobileTabletMax:[&>*]:!mx-auto mobileTabletMax:pt-[100px] px-[20px] lg:px-0 lg:max-w-[580px]">
						{
							items.map((item) => {
								if (item.__typename === 'ContentfulRichText') {
									return getText(item);
								} else if (item.__typename === 'ContentfulLink') {
									return <div className="flex justify-center mt-10">{getCta(item)}</div>;
								}
								return null;
							})
						}
					</div>
				</div>
				<div className="w-full justify-center relative flex  lg:justify-end">

					{imageUrl &&
						<Image
							src={imageUrl}
							alt={imageAlt}
							srcMobile={imageMobileUrl}
							className=" relative mobileTabletMax:z-[-1] mt-[-180px] lg:mt-0 w-full lg:w-max-content"
							pictureClassName="mobileTabletMax:w-full"
						/>
					}
				</div>
			</div>
		);
	}

	if (type === 'Connect With Rep') {
    // extend the below logic if needed for others
		const bannerStyleLongTitle = bannerId?.trim() === 'access-banner-left';
		return (
			<div className="flex flex-col lg:flex-row  mx-auto mt-[40px] items-center lg:mt-0 justify-center relative xl:max-w-full">
				<div className={`relative lg:absolute flex items-center justify-center z-10 ${bannerStyleLongTitle ? 'lg:left-[40%]' : ''}`} >
					<div className={`w-[400px] ${bannerStyleLongTitle ? 'w-full' : ''}`}>
						{
							items.map((item) => {
								if (item.__typename === 'ContentfulRichText') {
									if (item?.contentfulid?.trim() === 'hero-heading-1') {
										return getText({
											...item,
											style: "mb-5",
											nodeStyles: { paragraph: "font-asap_condensed uppercase !text-h1_xl xl:!text-h1_4xl " }
										});
									} else {
										return getText({
											...item,
											nodeStyles: { heading3: "text-h5 xl:text-h3 " }
										});
									}

								} else if (item.__typename === 'ContentfulLink') {
									return <div className="flex justify-center mt-10">{getCta(item)}</div>;
								}
								return null;
							})
						}
					</div>
				</div>
				<div className="lg:block relative ">
					{imageUrl && (
						<Image
							src={imageUrl}
							alt={imageAlt}
							srcMobile={imageMobileUrl}
							className="w-full"
							pictureClassName="mobileTabletMax:mt-[-90px] lg:ml-[120px]"
						/>
					)}
				</div>
			</div>
		);
	}

	if (type === 'Hero') {
		return (
			<HeroBanner id={content?.contentfulid} style={{ backgroundColor: !isMobile ? bgColor : 'white' }} className={`${!isMobile ? 'rounded-bottom_48' : ''}`}>
				<div className={`flex flex-col-reverse lg:flex-row  mx-auto items-center  relative justify-end lg:max-w-lg xl:max-w-default lg:pr-[40px] ${isMobile ? 'rounded-bottom_48' : ''}`} style={{ backgroundColor: isMobile ? bgColor : 'transparent' }}>
					{!isMobile && (
						<div className="hidden lg:left-[50px] lg:absolute lg:flex items-center xl:left-[0px] z-[1] ">
							<div className=" px-[100px] pt-[48px] pb-[48px] [&>div]:text-center lg:[&>div]:text-left lg:max-w-[563px] lg:p-0 lg:pt-[120px] content">
								{
									items.map((item) => {
										if (item.__typename === 'ContentfulRichText') {
											if (item?.contentfulid?.trim() === 'hero-heading-1') {
												return getText({
													...item,
													nodeStyles: { heading1: "!text-h2" }
												});
											} else {
												return getText(item);
											}

										} else if (item.__typename === 'ContentfulLink') {
											return <div className="flex justify-center mt-10">{getCta(item)}</div>;
										}
										return null;
									})
								}
							</div>
						</div>
					)}
					<div className="relative flex" >
						{imageUrl && (
							<Image
								src={imageUrl}
								srcMobile={imageMobileUrl}
								alt={imageAlt}
								style={{}}
								className="  lg:w-[720px] xl:w-full z-0 "
							/>
						)}
					</div>
				</div>

				{isMobile && (
					<div className=" block items-center">
						<div className=" px-[30px] pt-[48px] pb-[48px] [&>div]:text-center">
							{
								items.map((item) => {
									if (item.__typename === 'ContentfulRichText') {
										if (item?.contentfulid?.trim() === 'hero-heading-1') {
											return getText({
												...item,
												nodeStyles: { heading1: "!text-h4" },
												style: "mobileTabletMax:mx-auto mobileTabletMax:[&>*]:!text-dark_green"
											});
										} else {
											return getText({ ...item, style: "mobileTabletMax:mx-auto  mobileTabletMax:[&>*]:!text-dark_green" });
										}

									} else if (item.__typename === 'ContentfulLink') {
										return <div className="flex justify-center mt-10">{getCta(item)}</div>;
									}
									return null;
								})
							}
						</div>
					</div>
				)}
			</HeroBanner>
		);
	}

	if (type === 'Hero Gradient') {
		return (<div className="mx-auto absolute top-0 left-0 right-0 bottom-0  h-[500px] ">

			{imageUrl && (
				<Image
					src={imageUrl}
					srcMobile={imageMobileUrl}
					alt={imageAlt}
					style={{}}
					className=" mx-auto "
				/>
			)}
		</div>)
	}

	if (type === 'Safety') {
		return (
			<div className="flex flex-col lg:flex-row  mx-auto mt-[80px] items-center lg:mt-0 justify-center relative xl:max-w-full">
				<div className="relative lg:absolute flex items-center justify-center lg:right-[5px] xl:right-[75px] z-[1] top-[100px]">
					<div className="lg:max-w-[485px] xl:max-w-[670px]">
						{
							items.map((item) => {
								if (item.__typename === 'ContentfulRichText') {
									if (item?.contentfulid?.trim() === 'heading1') {
										return getText({
											...item,
											style: "mb-5",
											nodeStyles: {}
										});
									} else if (item?.contentfulid?.trim() === 'heading2') {
										return getText({
											...item,
											style: "mb-5",
											nodeStyles: { paragraph: "font-asap_condensed uppercase !text-h1_48 xl:!text-h1_64 " }
										});
									} else {
										return getText({
											...item,
											nodeStyles: {},
											style: "max-w-[485px] mx-auto"
										});
									}

								} else if (item.__typename === 'ContentfulLink') {
									return <div className="flex justify-center mt-10">{getCta(item)}</div>;
								}
								return null;
							})
						}
					</div>
				</div>
				<div className="lg:block relative ">
					{imageUrl && (
						<Image
							src={imageUrl}
							alt={imageAlt}
							srcMobile={imageMobileUrl}
							className="w-full"
							pictureClassName="mobileTabletMax:mt-[-90px]"
						/>
					)}
				</div>
			</div>
		)
	}


	if (type === 'Image Right') {
		return (<div className="banner-container relative mx-auto lg:p-[56px] px-[24px] py-[48px] rounded-all_48" style={{ backgroundColor: bgColor }}>
			<div className={`flex lg:flex-row flex-col laptop:w-full lg:w-[55%] xl:w-[70%] mobileTabletMax:items-center`}>
				<div className="banner-container--copy flex-1">
					{
						items.map((item) => {
							if (item.__typename === 'ContentfulRichText') {
								if (item?.componentId?.trim() === 'hero-heading-1') {
									return getText({
										...item,
										nodeStyles: { heading1: "!text-h2" }
									});
								} else {
									return getText(item);
								}
							} else if (item.__typename === 'ContentfulLink') {
								return <div className="flex justify-center mt-10">{getCta(item)}</div>;
							}
							return null;
						})
					}
				</div>
				{((imageUrl && !isMobile) || (isMobile && imageMobile)) && (
					<div className={`banner-container--img relative lg:my-0 lg:absolute top-[50%] right-0 lg:transform lg:translate-y-[-50%] xl:mr-[-2rem] lg:mr-[-2rem] laptop:mr-[-7rem]`}
						style={{
							marginTop: isMobile ? 'calc(-100px + 10vw)' : '',
							marginBottom: isMobile ? 'calc(-100px + 10vw)' : '',
						}}
						>
						<Image
							src={imageUrl}
							srcMobile={imageMobileUrl}
							alt={imageAlt}
							className={isMobile ? 'w-full mt-[-3rem]' : ''}
						/>
					</div>
				)}
			</div>
		</div>)
	}


	if (type === 'Image Left') {
		return (<div className="mx-auto absolute top-0 left-0 right-0 bottom-0  h-[500px] ">

			{imageUrl && (
				<Image
					src={imageUrl}
					srcMobile={imageMobileUrl}
					alt={imageAlt}
					style={{}}
					className=" mx-auto "
				/>
			)}
		</div>)
	}

	if (type === 'Administration Guidance') {
		return (<div className="mx-auto absolute top-0 left-0 right-0 bottom-0  h-[500px] ">

			{imageUrl && (
				<Image
					src={imageUrl}
					srcMobile={imageMobileUrl}
					alt={imageAlt}
					style={{}}
					className=" mx-auto "
				/>
			)}
		</div>)
	}

	if (type === 'Why Exparel Effective Pain Control') {
		return (
			<div className="flex flex-col-reverse lg:flex-row  mx-auto  items-center lg:mt-0 justify-center relative lg:max-w-lg xl:max-w-full">
				{backgroundImageUrl && (
					<Image
						src={backgroundImageUrl}
						alt={backgroundImageAlt}
						className="w-full lg:block hidden"
						pictureClassName="absolute bottom-0 z-[0]"
					/>
				)}
				<div className="lg:block lg:z-[2] lg:ml-[-270px] mobileTabletMax:w-full mobileTabletMax:mt-[-200px]">
					{imageUrl && (
						<Image
							src={imageUrl}
							alt={imageAlt}
							srcMobile={imageMobileUrl}
							className="mobileTabletMax:w-full"
							pictureClassName="mobileTabletMax:w-full"
						/>
					)}
				</div>

				<div className=" z-[1] lg:ml-[-100px] mobileTabletMax:mt-[70px] mobileTabletMax:mx-[20px]">
					{
						items.map((item) => {
							if (item.__typename === 'ContentfulRichText') {
								return getText({
									...item,
									nodeStyles: {},
									style: "max-w-[627px] w-full mx-auto "
								});
							}
						})
					}
				</div>
			</div>
		)
	}

  // Fallback for unknown variant
  return <ErrorComponent message={`Unknown banner variant: ${type}`} />;
};

Banner.propTypes = {
  content: PropTypes.shape({
    entryTitle: PropTypes.string,
	  type: PropTypes.oneOf(['Hero', 'Image Right', 'Image Left', 'Full Background', 'Hero Gradient']),
    title: PropTypes.shape({
      raw: PropTypes.string
    }),
    subTitle: PropTypes.shape({
      raw: PropTypes.string
    }),
    description: PropTypes.shape({
      raw: PropTypes.string
    }),
    image: PropTypes.shape({
      file: PropTypes.shape({
        fileName: PropTypes.string,
        url: PropTypes.string
      })
    }),
    cta: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      href: PropTypes.string,
      target: PropTypes.string,
      displayType: PropTypes.string
    }))
  }).isRequired
};

export default Banner;
